import dynamic from 'next/dynamic';
import React, { useContext } from 'react';
import { SearchModes } from '@/domainLogic/index.ts';
import { BADGES_LAYOUT, ResetPosition } from '@/app/components/search/components/NoResults/BadgesContainer/types.ts';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useSearchPageData } from '@/app/components/search/searchPageDataProvider.tsx';
import { APIStatusEnum } from '@/app/components/search/common/reloadData/APIStatusEnum';
import { SearchPageContext } from '@/app/components/search/components/SearchPageContainer/SearchPageContext.ts';
import Title from '@/app/components/search/components/Title';
import ListViewSort from '@/app/components/search/components/Filters/verticalFilters/Fields/getSortControl/ListViewSort';
import type { DynamicFiltersResponse } from '@/app/components/search/utils/api/dynamicFiltersResponseTypes';
import ErrorBoundary from '@/app/common/components/ErrorBoundary';

const BadgeContainer = dynamic(() => import('@/app/components/search/components/NoResults/BadgesContainer'), {
  ssr: false,
});

type Props = {
  dynamicFiltersResponse?: DynamicFiltersResponse;
};

const TitleLayout = ({ dynamicFiltersResponse }: Props) => {
  const { searchInfoData } = usePageWrapperData();
  const { productsData, isLoading } = useSearchPageData();
  const { isDesktop } = useCommonClient();
  const { listView } = useContext(SearchPageContext);

  const { searchMode } = searchInfoData;
  const { transport } = productsData;

  const isWaiting = productsData.apiStatus === APIStatusEnum.WAIT;
  const isHO = searchMode === SearchModes.HO;
  const hasProducts = (productsData?.productCounters?.productsCount || 0) > 0;
  const title = (
    <ErrorBoundary id="Title">
      <Title isWaiting={isWaiting || isLoading} transport={transport} dynamicFiltersResponse={dynamicFiltersResponse} />
    </ErrorBoundary>
  );

  const activeFiltersAndListViewSort =
    listView && isDesktop ? (
      <>
        {hasProducts && (
          <ErrorBoundary id="Results/BadgeContainer">
            <BadgeContainer
              resetPosition={ResetPosition.Left}
              style={{ marginBottom: 16, width: '100%' }}
              dynamicFiltersResponse={dynamicFiltersResponse}
              layout={BADGES_LAYOUT.MULTIPLE_ROWS}
            />
          </ErrorBoundary>
        )}
        <ErrorBoundary id="ListViewSort">
          <ListViewSort isLoading={isLoading} />
        </ErrorBoundary>
      </>
    ) : null;

  if (isHO)
    return (
      <>
        {title}
        {activeFiltersAndListViewSort}
      </>
    );

  return (
    <>
      {activeFiltersAndListViewSort}
      {title}
    </>
  );
};

TitleLayout.displayName = 'TitleLayout';

export default TitleLayout;
